import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ThemeToggle } from '../components/ThemeToggle'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

export function PrivacyPolicy() {
  const { lang } = useParams<{ lang: string }>()
  const { t } = useTranslation('legal')

  useEffect(() => {
    if (lang) {
      localStorage.setItem('i18nextLng', lang)
    }
  }, [lang])

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const currentLang = lang || 'en'

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted to-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6 transition-colors duration-200"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t('privacy.backToHome')}
            </Link>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              {t('privacy.title')}
            </h1>
            <p className="mt-4 text-muted-foreground">
              {t('privacy.lastUpdated')}
            </p>
          </div>

          {/* Content Card */}
          <motion.div
            className="bg-card rounded-2xl shadow-xl p-8 space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.introduction.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacy.introduction.text')}
              </p>
            </section>

            {/* GDPR Compliance Notice */}
            <section className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border-l-4 border-green-500">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('privacy.gdprCompliance.title')}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t('privacy.gdprCompliance.text')}
              </p>
            </section>

            {/* Data We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.dataWeCollect.title')}
              </h2>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">
                {t('privacy.dataWeCollect.personalData.title')}
              </h3>
              <p className="text-muted-foreground mb-3">
                {t('privacy.dataWeCollect.personalData.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataWeCollect.personalData.accountInfo')}
                  </strong>{' '}
                  {t('privacy.dataWeCollect.personalData.accountInfoValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataWeCollect.personalData.profileInfo')}
                  </strong>{' '}
                  {t('privacy.dataWeCollect.personalData.profileInfoValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataWeCollect.personalData.communicationData')}
                  </strong>{' '}
                  {t('privacy.dataWeCollect.personalData.communicationDataValue')}
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">
                {t('privacy.dataWeCollect.technicalData.title')}
              </h3>
              <p className="text-muted-foreground mb-3">
                {t('privacy.dataWeCollect.technicalData.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>{t('privacy.dataWeCollect.technicalData.ipAddress')}</li>
                <li>{t('privacy.dataWeCollect.technicalData.browser')}</li>
                <li>{t('privacy.dataWeCollect.technicalData.os')}</li>
                <li>{t('privacy.dataWeCollect.technicalData.referring')}</li>
                <li>{t('privacy.dataWeCollect.technicalData.pagesViewed')}</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">
                {t('privacy.dataWeCollect.cookies.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('privacy.dataWeCollect.cookies.text')}
              </p>
            </section>

            {/* How We Use Your Data */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.howWeUseData.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('privacy.howWeUseData.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong className="text-foreground">
                    {t('privacy.howWeUseData.accountManagement')}
                  </strong>{' '}
                  {t('privacy.howWeUseData.accountManagementValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.howWeUseData.authentication')}
                  </strong>{' '}
                  {t('privacy.howWeUseData.authenticationValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.howWeUseData.serviceDelivery')}
                  </strong>{' '}
                  {t('privacy.howWeUseData.serviceDeliveryValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.howWeUseData.communication')}
                  </strong>{' '}
                  {t('privacy.howWeUseData.communicationValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.howWeUseData.platformImprovement')}
                  </strong>{' '}
                  {t('privacy.howWeUseData.platformImprovementValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.howWeUseData.legalCompliance')}
                  </strong>{' '}
                  {t('privacy.howWeUseData.legalComplianceValue')}
                </li>
              </ul>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-l-4 border-blue-500 mt-6">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {t('privacy.howWeUseData.legalBasis.title')}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {t('privacy.howWeUseData.legalBasis.text')}
                </p>
              </div>
            </section>

            {/* Data Sharing and Disclosure */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.dataSharing.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('privacy.dataSharing.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSharing.serviceProviders')}
                  </strong>{' '}
                  {t('privacy.dataSharing.serviceProvidersValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSharing.companyAdmins')}
                  </strong>{' '}
                  {t('privacy.dataSharing.companyAdminsValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSharing.legalRequirements')}
                  </strong>{' '}
                  {t('privacy.dataSharing.legalRequirementsValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSharing.businessTransfers')}
                  </strong>{' '}
                  {t('privacy.dataSharing.businessTransfersValue')}
                </li>
              </ul>
            </section>

            {/* Your Data Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.dataRights.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('privacy.dataRights.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToAccess')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToAccessValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToRectification')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToRectificationValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToErasure')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToErasureValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToRestrict')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToRestrictValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToPortability')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToPortabilityValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToObject')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToObjectValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataRights.rightToWithdraw')}
                  </strong>{' '}
                  {t('privacy.dataRights.rightToWithdrawValue')}
                </li>
              </ul>

              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 border-l-4 border-purple-500 mt-6">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {t('privacy.dataRights.howToExercise.title')}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {t('privacy.dataRights.howToExercise.text')}{' '}
                  <a
                    href={`mailto:${t('privacy.dataRights.howToExercise.email')}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('privacy.dataRights.howToExercise.email')}
                  </a>
                  {t('privacy.dataRights.howToExercise.responseTime')}
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.dataSecurity.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('privacy.dataSecurity.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSecurity.encryption')}
                  </strong>{' '}
                  {t('privacy.dataSecurity.encryptionValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSecurity.accessControl')}
                  </strong>{' '}
                  {t('privacy.dataSecurity.accessControlValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSecurity.secureStorage')}
                  </strong>{' '}
                  {t('privacy.dataSecurity.secureStorageValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('privacy.dataSecurity.regularAudits')}
                  </strong>{' '}
                  {t('privacy.dataSecurity.regularAuditsValue')}
                </li>
              </ul>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.dataRetention.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('privacy.dataRetention.text')}
              </p>
            </section>

            {/* International Data Transfers */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.internationalTransfers.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('privacy.internationalTransfers.text')}
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.childrensPrivacy.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('privacy.childrensPrivacy.text')}
              </p>
            </section>

            {/* Updates to This Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.updates.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('privacy.updates.text')}
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-muted rounded-xl p-6">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('privacy.contact.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('privacy.contact.intro')}
              </p>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <strong className="text-foreground">
                    {t('privacy.contact.email')}
                  </strong>{' '}
                  <a
                    href="mailto:info@securesight.ai"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    info@securesight.ai
                  </a>
                </p>
                <p>
                  <strong className="text-foreground">
                    {t('privacy.contact.address')}
                  </strong>{' '}
                  <br />
                  {t('privacy.contact.addressValue')}
                </p>
                <p>
                  <strong className="text-foreground">
                    {t('privacy.contact.dpo')}
                  </strong>{' '}
                  {t('privacy.contact.dpoValue')}
                </p>
              </div>
            </section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
